import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, School, Settings, Users, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@/components/common/States";
import { fetcher } from "@/lib/api";
import { RESELLER_STATUS, TENANT_STATUS, rupiah } from "@/lib/labels";

const Stat = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" aria-hidden /> <p className="text-sm font-medium">{label}</p></div>
    <p className="font-brand text-2xl md:text-3xl font-extrabold mt-2">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Baris = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-1.5" data-testid={testId}>
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right break-all">{value || "—"}</span>
  </div>
);

export default function MitraHome() {
  const q = useQuery({ queryKey: ["mitra", "beranda"], queryFn: () => fetcher("/reseller/beranda") });

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const r = d.reseller;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Ruang Mitra SIST" title={`Selamat datang, ${r.name}`}
        description="Ruang kerja reseller: pantau sekolah binaan dan angka langganan periode berjalan. Data dihitung dari tagihan akun yang reseller layani."
        actions={
          <Button asChild data-testid="mitra-home-settings-button">
            <Link to="/mitra/pengaturan"><Settings className="h-4 w-4 mr-1.5" /> Pengaturan</Link>
          </Button>
        } />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat icon={School} label="Sekolah binaan" value={d.sekolah_binaan.length} testId="mitra-stat-schools" />
        <Stat icon={Users} label="Akun tertagih" value={d.akun_bulan_ini} hint={`Periode ${d.periode}`} testId="mitra-stat-accounts" />
        <Stat icon={Wallet} label="Nominal tagihan" value={rupiah(d.nominal_jual_bulan_ini)} hint="Harga jual × akun bulan ini" testId="mitra-stat-nominal" />
        <Stat icon={Settings} label="Harga jual saya" value={rupiah(r.retail_price_idr)} hint={`Plafon ${rupiah(d.pengaturan.retail_price_cap_idr)}`} testId="mitra-stat-price" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border bg-card p-6" data-testid="mitra-profile-section">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base md:text-lg font-bold">Profil reseller</h2>
            <StatusBadge map={RESELLER_STATUS} value={r.status} testId="mitra-status-badge" />
          </div>
          <div className="mt-3 divide-y">
            <Baris label="Kode referral" value={r.referral_code} testId="mitra-profile-referral" />
            <Baris label="Nama kontak" value={r.contact_name} />
            <Baris label="Telepon/WA" value={r.phone} />
            <Baris label="Email reseller" value={r.email} />
            <Baris label="Label QRIS" value={r.qris_label} />
            <Baris label="NMID QRIS" value={r.qris_nmid} />
            <Baris label="Rekening bank" value={r.bank_account} />
            <Baris label="Akun login" value={d.login_email} testId="mitra-profile-login-email" />
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Kanal pembayaran dan harga jual diatur di halaman <Link to="/mitra/pengaturan" className="underline font-medium" data-testid="mitra-profile-settings-link">Pengaturan</Link>.
          </p>
        </section>

        <section className="rounded-2xl border bg-card p-6" data-testid="mitra-schools-section">
          <h2 className="text-base md:text-lg font-bold">Sekolah binaan</h2>
          {d.sekolah_binaan.length === 0 ? (
            <div className="mt-3">
              <EmptyState title="Belum ada sekolah binaan"
                description="Sekolah masuk daftar ini setelah Pusat Kendali menerima pengajuan dan menugaskannya kepada Anda. Ajukan lewat menu Daftar Sekolah." />
            </div>
          ) : (
            <ul className="mt-3 divide-y">
              {d.sekolah_binaan.map((s) => (
                <li key={s.id} className="py-2.5" data-testid={`mitra-school-${s.id}`}>
                  <Link to={`/mitra/pelanggan/${s.id}`} className="flex items-center justify-between gap-3 group">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate group-hover:underline">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.city || "—"}</p>
                    </div>
                    <StatusBadge map={TENANT_STATUS} value={s.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3">
            <Button asChild variant="outline" size="sm" data-testid="mitra-schools-open">
              <Link to="/mitra/pelanggan">Buka Kelola Pelanggan <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </div>
        </section>
      </div>

      <div className="rounded-2xl border bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 border-blue-200 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="mitra-roadmap-notice">
        <p className="text-sm text-blue-900 dark:text-blue-100 max-w-2xl">
          Kelola pelanggan dan form Pendaftaran Sekolah sudah aktif di menu sebelah. Menu berikutnya yang sedang disiapkan:
          halaman langganan sekolah dan alur pembayaran sekolah ke reseller.
        </p>
        <Button asChild variant="outline" data-testid="mitra-roadmap-cta">
          <Link to="/mitra/daftar">Daftarkan Sekolah <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
        </Button>
      </div>
    </div>
  );
}
